import { useState } from 'react'
import { IoChevronDown, IoChevronUp, IoAdd, IoCheckmark } from 'react-icons/io5'
import type { PlayerSOTItem } from '../../types'
import { COUNTRY_FLAGS } from '../../context/LeagueContext'

interface PlayerStatsCardProps {
  item: PlayerSOTItem
}

function formatKickoff(isoDate: string): string {
  const date = new Date(isoDate)
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const day = days[date.getDay()]
  const hours = date.getHours().toString().padStart(2, '0')
  const mins = date.getMinutes().toString().padStart(2, '0')
  return `${day} ${hours}:${mins}`
}

function getPositionClass(position: string | null): string {
  if (!position) return ''
  const pos = position.toLowerCase()
  if (['attacker', 'forward', 'striker', 'f', 'st', 'cf', 'lw', 'rw'].includes(pos)) return 'attacker'
  if (['midfielder', 'midfield', 'm', 'cm', 'dm', 'am', 'lm', 'rm', 'cdm', 'cam'].includes(pos)) return 'midfielder'
  if (['defender', 'defence', 'd', 'cb', 'lb', 'rb', 'lwb', 'rwb'].includes(pos)) return 'defender'
  return ''
}

function getPositionLabel(position: string | null): string {
  if (!position) return '-'
  const pos = position.toLowerCase()
  if (['attacker', 'forward', 'striker', 'f', 'st', 'cf', 'lw', 'rw'].includes(pos)) return 'FWD'
  if (['midfielder', 'midfield', 'm', 'cm', 'dm', 'am', 'lm', 'rm', 'cdm', 'cam'].includes(pos)) return 'MID'
  if (['defender', 'defence', 'd', 'cb', 'lb', 'rb', 'lwb', 'rwb'].includes(pos)) return 'DEF'
  if (['goalkeeper', 'g', 'gk'].includes(pos)) return 'GK'
  return pos.substring(0, 3).toUpperCase()
}

function getProbabilityClass(prob: number): string {
  if (prob >= 60) return 'prob-high'
  if (prob >= 40) return 'prob-med'
  return 'prob-low'
}

function getCountryFlag(country: string | null): string {
  if (!country) return ''
  return COUNTRY_FLAGS[country] || ''
}

export function PlayerStatsCard({ item }: PlayerStatsCardProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isAdded, setIsAdded] = useState(false)

  const handleAddToBuilder = (e: React.MouseEvent) => {
    e.stopPropagation()
    // TODO: Integrate with bet builder context
    setIsAdded(true)
  }

  return (
    <div className="player-stats-card" onClick={() => setIsExpanded(!isExpanded)}>
      <div className="player-stats-card-header">
        <div className="player-stats-card-player">
          <span className="name">{item.player_name}</span>
          <span className={`position-badge ${getPositionClass(item.position)}`}>
            {getPositionLabel(item.position)}
          </span>
        </div>
        <span className={`prob-badge ${getProbabilityClass(item.conviction_pct)}`}>
          {item.conviction_pct}%
        </span>
      </div>

      <div className="player-stats-card-fixture">
        <span>{getCountryFlag(item.league_country)} {item.fixture_label}</span>
        <span> - {formatKickoff(item.kickoff_utc)}</span>
      </div>

      <div className="player-stats-card-stats">
        <div className="player-stats-card-stat">
          <span className="label">Avg SOT</span>
          <span className="value">{item.last5_avg.toFixed(1)}</span>
        </div>
        <div className="player-stats-card-stat">
          <span className="label">Exp Mins</span>
          <span className="value">{item.expected_minutes != null ? Math.round(item.expected_minutes) : '-'}</span>
        </div>
        <div className="player-stats-card-stat">
          <span className="label">Opp SOT</span>
          <span className="value">{item.opp_sot_conceded.toFixed(1)}</span>
        </div>
      </div>

      {isExpanded && (
        <div className="player-stats-card-expanded">
          <div className="player-stats-card-stats">
            <div className="player-stats-card-stat">
              <span className="label">Season Avg</span>
              <span className="value">{item.season_avg != null ? item.season_avg.toFixed(2) : '-'}</span>
            </div>
            <div className="player-stats-card-stat">
              <span className="label">Start Prob</span>
              <span className="value">{item.start_probability != null ? `${Math.round(item.start_probability * 100)}%` : '-'}</span>
            </div>
            <div className="player-stats-card-stat">
              <span className="label">Role</span>
              <span className="value">{item.role || '-'}</span>
            </div>
          </div>
          <div className="player-stats-card-stats">
            <div className="player-stats-card-stat">
              <span className="label">SOT/Start</span>
              <span className="value">{item.sot_per_start != null ? item.sot_per_start.toFixed(2) : '-'}</span>
            </div>
            <div className="player-stats-card-stat">
              <span className="label">SOT/Sub</span>
              <span className="value">{item.sot_per_sub != null ? item.sot_per_sub.toFixed(2) : '-'}</span>
            </div>
            <div className="player-stats-card-stat">
              <span className="label">Pos Factor</span>
              <span className="value">{item.position_factor.toFixed(2)}</span>
            </div>
          </div>
          <div className="player-stats-card-why">
            {item.why || 'No analysis available'}
          </div>
        </div>
      )}

      <div className="player-stats-card-actions">
        <button
          className="expand-toggle"
          onClick={(e) => {
            e.stopPropagation()
            setIsExpanded(!isExpanded)
          }}
        >
          {isExpanded ? <IoChevronUp size={16} /> : <IoChevronDown size={16} />}
        </button>
        <button
          className={`player-stats-add-btn ${isAdded ? 'added' : ''}`}
          onClick={handleAddToBuilder}
          disabled={isAdded}
        >
          {isAdded ? (
            <>
              <IoCheckmark size={14} /> Added
            </>
          ) : (
            <>
              <IoAdd size={14} /> Add
            </>
          )}
        </button>
      </div>
    </div>
  )
}
